import { Big } from 'big.js';
import { match } from 'ts-pattern';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderType } from '~/tenants/bre/model/enums';
import BreLetterheadsConfig, {
  LetterheadPaper
} from '~/tenants/bre/performable/print/letterheads/BreLetterheadsConfig';
import { BreLetterheadsPay, BreLetterheadsRate } from '~/tenants/bre/performable/print/letterheads/BreLetterheadsData';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import { datetime } from '~/lib/datettime';

export default class BreLetterheadsJob extends BreJob<typeof BreLetterheadsConfig> {
  get performable(): typeof BreLetterheadsConfig {
    return BreLetterheadsConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.AGENT_MKTG);
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Count',
      value: this.metadata.count,
      provider: true,
    });

    info.push({
      name: 'Paper',
      value: this.paperType,
      provider: true,
    });

    info.push({
      name: 'Need By',
      value: datetime(this.metadata.need_by, this.order.location.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  get paperType(): string {
    const { paper } = this.metadata;
    return BreLetterheadsJob.paperType(paper);
  }

  static paperType(paper: LetterheadPaper): string {
    return match(paper)
      .with('smooth_white', () => 'Smooth White')
      .exhaustive();
  }

  isDelivery(): boolean {
    return true;
  }

  isPrint(): boolean {
    return true;
  }

  isFreeDelivery(): boolean {
    return true;
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreLetterheadsConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      count: '250',
      paper: 'smooth_white',
      need_by: datetime(this.order.created, this.order.timezone).toSQLDate(),
    };
  }

  revenueLines(): TenantJobLine[] {
    const { count, paper } = this.metadata;
    const rate = BreLetterheadsRate[paper][count];
    const lines: TenantJobLine[] = [];

    if (rate) {
      lines.push({
        id: 'letterheads_print',
        description: `${this.paperType} letterheads (x${count})`,
        amount: new Big(rate),
        taxable: true,
      });
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { count, paper } = this.metadata;
    const rate = BreLetterheadsPay[paper][count];
    const lines: TenantJobLine[] = [];

    if (rate) {
      lines.push({
        id: 'letterheads_print',
        description: `${this.paperType} letterheads (x${count})`,
        amount: new Big(rate),
      });
    }

    return lines;
  }
}
